import { com, Nullable } from '@eidu/web'
import EqualityHashMap, { EqualityHashMapKey } from '../../../../../util/EqualityHashMap'
import Entity = com.eidu.sharedlib.entity.Entity
import EntitySnapshot = com.eidu.sharedlib.entity.publish.EntitySnapshot
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import LinkKey = com.eidu.sharedlib.entity.link.LinkKey

export type FieldChange = {
  fieldId: FieldId
  before: Nullable<FieldValue> | undefined
  after: Nullable<FieldValue> | undefined
}

export type LinkChange = {
  key: LinkKey
  before: string | undefined
  after: string | undefined
}

export type EntitySnapshotDiff = {
  fieldChanges: FieldChange[]
  linkChanges: LinkChange[]
  equal: boolean
}

const valuesEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true
  if (a !== null && typeof a === 'object' && 'equals' in a && typeof (a as { equals: unknown }).equals === 'function')
    return (a as { equals: (other: unknown) => boolean }).equals(b)
  return false
}

const diffMapEntries = <K extends EqualityHashMapKey, V>(
  left: ReadonlyMap<K, V>,
  right: ReadonlyMap<K, V>
): { key: K; before: V | undefined; after: V | undefined }[] => {
  const leftEq = new EqualityHashMap<K, V>(Array.from(left))
  const rightEq = new EqualityHashMap<K, V>(Array.from(right))
  const out: { key: K; before: V | undefined; after: V | undefined }[] = []
  for (const [k, v] of leftEq) {
    if (!rightEq.has(k)) out.push({ key: k, before: v, after: undefined })
    else if (!valuesEqual(v, rightEq.get(k))) out.push({ key: k, before: v, after: rightEq.get(k) })
  }
  for (const [k, v] of rightEq) {
    if (!leftEq.has(k)) out.push({ key: k, before: undefined, after: v })
  }
  return out
}

type FieldKey = FieldId & EqualityHashMapKey
type LinkKeyEq = LinkKey & EqualityHashMapKey

const diffFromMaps = (
  beforeFields: ReadonlyMap<FieldId, Nullable<FieldValue>>,
  afterFields: ReadonlyMap<FieldId, Nullable<FieldValue>>,
  beforeLinks: ReadonlyMap<LinkKey, string>,
  afterLinks: ReadonlyMap<LinkKey, string>
): EntitySnapshotDiff => {
  const fieldEntries = diffMapEntries(
    beforeFields as unknown as ReadonlyMap<FieldKey, Nullable<FieldValue>>,
    afterFields as unknown as ReadonlyMap<FieldKey, Nullable<FieldValue>>
  )
  const linkEntries = diffMapEntries(
    beforeLinks as unknown as ReadonlyMap<LinkKeyEq, string>,
    afterLinks as unknown as ReadonlyMap<LinkKeyEq, string>
  )
  const fieldChanges: FieldChange[] = fieldEntries.map((e) => ({ fieldId: e.key, before: e.before, after: e.after }))
  const linkChanges: LinkChange[] = linkEntries.map((e) => ({ key: e.key, before: e.before, after: e.after }))
  return { fieldChanges, linkChanges, equal: fieldChanges.length === 0 && linkChanges.length === 0 }
}

const diffEntityVsSnapshot = (current: Entity, snapshot: EntitySnapshot): EntitySnapshotDiff =>
  diffFromMaps(
    snapshot.entity.valuesByFieldId.asJsReadonlyMapView(),
    current.valuesByFieldId.asJsReadonlyMapView(),
    snapshot.entity.links.asJsReadonlyMapView(),
    current.links.asJsReadonlyMapView()
  )

export const diffSnapshots = (left: EntitySnapshot, right: EntitySnapshot): EntitySnapshotDiff =>
  diffFromMaps(
    left.entity.valuesByFieldId.asJsReadonlyMapView(),
    right.entity.valuesByFieldId.asJsReadonlyMapView(),
    left.entity.links.asJsReadonlyMapView(),
    right.entity.links.asJsReadonlyMapView()
  )

export default diffEntityVsSnapshot
